import { ClientProxy } from '../../../../base/proxy/clientProxy'
import {
  CompayCancelRequest,
  CompayCancelResponse,
  CompayDashboardRequest,
  CompayDashboardResponse,
  CompayPaymentCategorySettings,
  CompayPaymentContractReport,
  CompayPaymentDetailRequest,
  CompayPaymentDetailResponse,
  CompayPaymentListRequest,
  CompayPaymentListResponse,
  CompayPaymentSettings,
  ShoppingCreditListRequest,
  ShoppingCreditListResponse,
} from '../models/models'

const normalizeCompayPaymentListResponse = (data: any): CompayPaymentListResponse => {
  if (!data) {
    return {
      Payments: [],
      TotalCount: 0,
      TotalAmount: 0,
      TotalAmountTL: 0,
      SuccessCount: 0,
      FailCount: 0,
      PendingCount: 0,
      TotalDealerMarkup: 0,
    }
  }

  // Old shape
  if (Array.isArray(data.Payments)) {
    return {
      Payments: data.Payments ?? [],
      TotalCount: data.TotalCount ?? 0,
      TotalAmount: data.TotalAmount ?? 0,
      TotalAmountTL: data.TotalAmountTL ?? 0,
      SuccessCount: data.SuccessCount ?? 0,
      FailCount: data.FailCount ?? 0,
      PendingCount: data.PendingCount ?? 0,
      TotalDealerMarkup: data.TotalDealerMarkup ?? 0,
    }
  }

  // New shape (as seen in response): { Items, TotalCount, PageNumber, ... , Summary: { ... } }
  const summary = data.Summary ?? {}
  return {
    Payments: Array.isArray(data.Items) ? data.Items : [],
    TotalCount: data.TotalCount ?? summary.TotalCount ?? 0,
    TotalAmount: summary.TotalAmount ?? data.TotalAmount ?? 0,
    TotalAmountTL: summary.TotalAmount ?? data.TotalAmountTL ?? 0,
    SuccessCount: summary.CompletedCount ?? summary.SuccessCount ?? 0,
    FailCount: summary.FailedCount ?? summary.FailCount ?? 0,
    PendingCount: summary.PendingCount ?? 0,
    TotalDealerMarkup: summary.TotalDealerProfit ?? summary.TotalDealerMarkup ?? 0,
  }
}

// Backend boş sonuç döndüğünde Credits null gelebiliyor; "veri yok" ile "hata" ayrımını
// bozmamak için burada normalize ediliyor. Success alanı yoksa başarılı kabul edilir.
const normalizeShoppingCreditListResponse = (data: any): ShoppingCreditListResponse => {
  if (!data) {
    return {
      Success: false,
      Credits: [],
      TotalCount: 0,
      TotalBaseAmount: 0,
      TotalFinalAmount: 0,
      TotalDealerMarkup: 0,
      ApprovedCount: 0,
      RejectedCount: 0,
      PendingCount: 0,
      DealerCreditCount: 0,
      CustomerCreditCount: 0,
    }
  }

  return {
    Success: data.Success !== false,
    Credits: Array.isArray(data.Credits) ? data.Credits : [],
    TotalCount: data.TotalCount ?? 0,
    PageNumber: data.PageNumber,
    PageSize: data.PageSize,
    TotalPages: data.TotalPages,
    TotalBaseAmount: data.TotalBaseAmount ?? 0,
    TotalFinalAmount: data.TotalFinalAmount ?? 0,
    TotalDealerMarkup: data.TotalDealerMarkup ?? 0,
    ApprovedCount: data.ApprovedCount ?? 0,
    RejectedCount: data.RejectedCount ?? 0,
    PendingCount: data.PendingCount ?? 0,
    DealerCreditCount: data.DealerCreditCount ?? 0,
    CustomerCreditCount: data.CustomerCreditCount ?? 0,
    ErrorMessage: data.ErrorMessage,
  }
}

// Compay Ödeme Listesi API
export class GetCompayPaymentListApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/payment-list',
      ...props,
    })
  }

  async getPaymentList(request: CompayPaymentListRequest): Promise<CompayPaymentListResponse> {
    const data = await this.postAsync<any>(request)
    return normalizeCompayPaymentListResponse(data)
  }
}

// Compay Ödeme Detay API
export class GetCompayPaymentDetailApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/payment-detail',
      ...props,
    })
  }

  async getPaymentDetail(request: CompayPaymentDetailRequest): Promise<CompayPaymentDetailResponse> {
    return await this.postAsync<CompayPaymentDetailResponse>(request)
  }
}

// Alışveriş Kredisi Listesi API
export class GetShoppingCreditListApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/credit-list',
      ...props,
    })
  }

  async getCreditList(request: ShoppingCreditListRequest): Promise<ShoppingCreditListResponse> {
    const data = await this.postAsync<any>(request)
    return normalizeShoppingCreditListResponse(data)
  }
}

// Compay İptal/İade API
export class CompayCancelApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/cancel',
      ...props,
    })
  }

  async cancelPayment(request: CompayCancelRequest): Promise<CompayCancelResponse> {
    return await this.postAsync<CompayCancelResponse>(request)
  }
}

// Compay Dashboard API
export class GetCompayDashboardApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/dashboard',
      ...props,
    })
  }

  async getDashboard(request: CompayDashboardRequest): Promise<CompayDashboardResponse> {
    return await this.postAsync<CompayDashboardResponse>(request)
  }
}

// Excel Export API
export class ExportCompayPaymentsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/export-payments',
      ...props,
    })
  }

  async exportToExcel(request: CompayPaymentListRequest): Promise<Blob> {
    return await this.postAsync<Blob>(request)
  }
}

export class ExportShoppingCreditsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/export-credits',
      ...props,
    })
  }

  async exportToExcel(request: ShoppingCreditListRequest): Promise<Blob> {
    return await this.postAsync<Blob>(request)
  }
}

export class UpdateSettingsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/update-settings',
      ...props,
    })
  }

  async updateSettings(request: CompayPaymentSettings): Promise<any> {
    return await this.postAsync<any>(request)
  }
}

export class GetSettingsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/settings',
      ...props,
    })
  }

  async getSettings(): Promise<CompayPaymentSettings> {
    return await this.getAsync<CompayPaymentSettings>()
  }
}

export class GetCategorySettingsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/get-category-settings',
      ...props,
    })
  }

  async getCategorySettings(): Promise<CompayPaymentCategorySettings[]> {
    return await this.getAsync<CompayPaymentCategorySettings[]>()
  }
}

export class UpdateCategorySettingsApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/update-category-settings',
      ...props,
    })
  }

  async updateCategorySettings(request: CompayPaymentCategorySettings[]): Promise<CompayPaymentCategorySettings[]> {
    return await this.postAsync<CompayPaymentCategorySettings[]>(request)
  }
}

export class ContractReportApi extends ClientProxy {
  constructor(props: any) {
    super({
      url: 'admin/compay-payment/contract-report',
      ...props,
    })
  }

  async getContractReport(): Promise<CompayPaymentContractReport[]> {
    return await this.getAsync<CompayPaymentContractReport[]>()
  }
}